"use client";

/**
 * Shared browser EventSource over the authenticated SSE stream
 * (/api/events/stream). A single connection is multiplexed across all
 * per-event subscribers (addEventSourceListener) so a page with several live
 * widgets opens ONE stream. The server gates the stream to the logged-in user's
 * own channels; events arrive named by their `type` (e.g. 'games.maze.generated').
 */

type Payload = Record<string, unknown>;
type Listener = (payload: Payload) => void;

const STREAM_PATH = "/api/events/stream";

let es: EventSource | null = null;
const listeners = new Map<string, Set<Listener>>();
const attached = new Set<string>();

function ensureSource(): EventSource | null {
  if (typeof window === "undefined") return null;
  if (!es) {
    es = new EventSource(STREAM_PATH);
    // Re-attach native listeners after an auto-reconnect drops them.
    es.onopen = () => attached.forEach(attachNative);
  }
  return es;
}

function attachNative(eventType: string) {
  const source = ensureSource();
  if (!source) return;
  source.addEventListener(eventType, (e: MessageEvent) => {
    let payload: Payload = {};
    try {
      payload = JSON.parse(e.data) as Payload;
    } catch {
      /* non-JSON frame */
    }
    listeners.get(eventType)?.forEach((fn) => fn(payload));
  });
}

/** Subscribe to one named SSE event; returns an unsubscribe fn. */
export function addEventSourceListener(eventType: string, fn: Listener): () => void {
  let set = listeners.get(eventType);
  if (!set) {
    set = new Set();
    listeners.set(eventType, set);
  }
  set.add(fn);

  if (!attached.has(eventType)) {
    attached.add(eventType);
    attachNative(eventType);
  }

  return () => {
    set!.delete(fn);
  };
}
